/**
 * @file index.tsx
 *
 * @fileoverview Renders unpublished job message and
 * limits job details overview.
 */
import Link from '~/jobiqo/components/Link';
import { JobDetailViewProps } from '~/jobiqo/components/Job/Detail/View';
import { useRouter } from 'next/router';
import { getIsJobUnpublished } from '~/jobiqo/components/Job/Detail/utils';
import { getIsJobPaused } from '~/jobiqo/components/Job/Detail/utils';
import { FormattedMessage } from 'react-intl';
import styles from './container.module.scss';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const JOB_DETAIL_VIEW_KEY = 'view',
  JOB_DETAIL_VIEW_FULL = 'full';

interface JobDetailContainerProps
  extends Pick<JobDetailViewProps, 'jobsPage' | 'job'> {
  /**
   * Children element.
   */
  children: React.ReactNode;
}

export const JobDetailContainer = ({
  children,
  job,
  jobsPage,
}: JobDetailContainerProps) => {
  const router = useRouter();
  const { JOBS } = useFrontendConfig();
  const UNPUBLISHED_JOB_FADE_HEIGHT_PX = JOBS.UNPUBLISHED_JOB_FADE_HEIGHT_PX;
  const isJobUnpublished = getIsJobUnpublished(job);
  const isJobPaused = getIsJobPaused(job);
  const isUnpublishedJobFadePositive = +UNPUBLISHED_JOB_FADE_HEIGHT_PX > 0;
  const isFullViewParamSet =
    router.query[JOB_DETAIL_VIEW_KEY] === JOB_DETAIL_VIEW_FULL;

  return (
    <>
      {(isJobUnpublished || isJobPaused) && (
        <MessageAlert alertStyle="warning">
          <div data-test-id="job_detail_job_no_longer_active">
            <Text marginTop={0} fontSize={4} textAlign="center">
              <FormattedMessage
                id="job_detail_job_no_longer_active"
                defaultMessage="This job ad is no longer active. Search for {search_url}"
                values={{
                  search_url: (
                    <Link href={jobsPage}>
                      <FormattedMessage
                        id="job_detail_link_to_search"
                        defaultMessage="New jobs"
                      />
                    </Link>
                  ),
                }}
              />
            </Text>
          </div>
        </MessageAlert>
      )}
      {(isJobUnpublished || isJobPaused) &&
      !job.canEdit &&
      isUnpublishedJobFadePositive &&
      !isFullViewParamSet ? (
        <div
          className={styles.contentFade}
          style={{
            height: `${UNPUBLISHED_JOB_FADE_HEIGHT_PX}px`,
          }}
          data-test-id="job_detail_container_fade"
        >
          {children}
          <div
            id="overlay"
            className={styles.contentFadeOverlay}
            style={{
              height: `${(+UNPUBLISHED_JOB_FADE_HEIGHT_PX / 3) * 2}px`,
            }}
          ></div>
        </div>
      ) : (
        <div data-test-id="job_detail_container">{children}</div>
      )}
    </>
  );
};

export default JobDetailContainer;
